import { useEffect, useState } from "react";
import { ProjectForm } from "@stories/molecules/ProjectForm/ProjectForm";
import { SiteOfficeForm } from "@stories/molecules/SiteOfficeForm/SiteOfficeForm";
import { createDefaultProject } from "@interfaces/project";
import { Steps } from "@stories/atoms/Steps/Steps";
import { useNavigate, useParams } from "react-router-dom";
import { SocialRequirementForm } from "@stories/molecules/SocialRequirementForm/SocialRequirementForm";
import { MPSGForm } from "@stories/molecules/MPSGForm/MPSGForm";
import { useProject } from "@hooks/crud/useProject";
import { useProfileContext } from "@hooks/context/useProfileContext";
import * as RouteHelper from "@utils/routes";
import { PageBreadcrumbs } from "@stories/atoms/PageBreadcrumbs/PageBreadcrumbs";
import { SubTitleWithImage } from "@stories/atoms/SubTitle/SubTitle";
import { SocialCommitmentLineItem } from "@interfaces/commitment";
import { ProjectLoader } from "@stories/atoms/Loaders/ProjectLoader/ProjectLoader";
import { StyledCard } from "@stories/atoms/StyledCard/StyledCard";
import { Grid } from "@mui/material";
import { ToastOptions, toast } from "react-toastify";

const steps = [
  "Project Details",
  "Site Office Details",
  "Deemed Hours",
  "Social Procurement Targets",
];

export const ProjectConfiguration = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();

  const { userProfile, userOrganisation } = useProfileContext();

  const { isProjectLoading, selectedProject, createProject, updateProject } =
    useProject(undefined, projectId);

  const [activeStep, setActiveStep] = useState(0);

  const [project, setProject] = useState(createDefaultProject());

  useEffect(() => {
    if (selectedProject) {
      setProject(selectedProject);
    } else {
      if (userProfile && userOrganisation) {
        setProject(createDefaultProject(userProfile, userOrganisation));
      }
    }
  }, [selectedProject, userProfile, userOrganisation]);

  if (
    isProjectLoading ||
    !userProfile ||
    !userOrganisation ||
    (projectId && !selectedProject)
  ) {
    console.log("Project configuration PAGE LOADER");
    return <ProjectLoader />;
  }

  const onSubmit = async (commitments: SocialCommitmentLineItem[]) => {
    try {
      project.commitments = commitments;
      if (projectId) {
        await updateProject(project);
        toast(`Project updated!`, {
          type: "success",
        } as ToastOptions);
      } else {
        await createProject(project);
        navigate(RouteHelper.listProjects);
      }
    } catch (e) {
      toast(`Could not update project, contact support!`, {
        type: "success",
      } as ToastOptions);
    }
  };

  const handleNext = () => {
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };
  const selectedCreateProjectStep = (() => {
    switch (activeStep) {
      default:
      case 0:
        return (
          <ProjectForm
            handleNext={handleNext}
            project={selectedProject || project}
            setProject={setProject}
          />
        );
      case 1:
        return (
          <SiteOfficeForm
            handleBack={handleBack}
            handleNext={handleNext}
            project={selectedProject || project}
            setProject={setProject}
          />
        );
      case 2:
        return (
          <MPSGForm
            handleBack={handleBack}
            handleNext={handleNext}
            project={selectedProject || project}
            setProject={setProject}
          />
        );
      case 3:
        return (
          <SocialRequirementForm
            loading={isProjectLoading}
            handleBack={handleBack}
            project={selectedProject || project}
            commitmentLineItems={
              selectedProject?.commitments || project?.commitments
            }
            onSubmit={onSubmit}
          />
        );
    }
  })();
  const title = selectedProject ? "Edit Project" : "Create Project";

  const breadcrumbs = [
    selectedProject
      ? {
          path: RouteHelper.project(projectId),
          name: selectedProject ? selectedProject.projectName : "New Project",
        }
      : {
          path: RouteHelper.project(),
          name: "New Project",
        },
  ];
  return (
    <>
      <PageBreadcrumbs
        title={title}
        organisationLogo={userOrganisation.organisationLogo}
        links={breadcrumbs}
      />

      <StyledCard>
        <Grid container spacing={2} sx={{ padding: "12px" }}>
          <Grid item xs={12} md={12}>
            <Steps activeStep={activeStep} steps={steps} />
          </Grid>
        </Grid>
      </StyledCard>
      {selectedCreateProjectStep}
    </>
  );
};
